import {
  Survey,
  SurveyAnswers,
  SurveyElement,
  SurveyElementType
} from 'core/SurveyModels'

export interface SurveyAnswersMapperItem {
  question: SurveyElement
  answer: SurveyAnswers['page']['element']
  pageName: string
  elementName: string
}

export class SurveyAnswersMapper {
  constructor(
    private survey: Survey,
    private answers: SurveyAnswers
  ) {}

  /**
   * Extract specific type of questions
   */
  public queryAnswers(
    evaluate: (
      element: SurveyElement,
      value: SurveyAnswers['page']['element']
    ) => boolean
  ) {
    const answers: SurveyAnswers = {}
    Object.keys(this.answers).forEach((pageName) => {
      Object.keys(this.answers[pageName]).forEach((elementName) => {
        const pageIndex = this.survey.pages.findIndex(
          (page) => page.name === pageName
        )
        const elementIndex = this.survey.pages[pageIndex].elements.findIndex(
          (el) => el.name === elementName
        )
        const value = this.answers?.[pageName]?.[elementName]
        if (
          evaluate(this.survey.pages[pageIndex].elements[elementIndex], value)
        ) {
          if (!answers[pageName]) answers[pageName] = {}
          answers[pageName][elementName] = this.answers[pageName][elementName]
        }
      })
    })
    return this.chronoSort(answers)
  }

  getAnswersFromType(type: SurveyElementType) {
    return this.queryAnswers((element) => element.type === type)
  }

  getPriorityAnsers() {
    return this.queryAnswers((element) => element.priority === true)
  }

  /**
   * Map all answers to a question-answers object
   * If no answers are passed it will use the SurveyAnswersMapper.answers
   */
  getQuestionAnswerMeta(rawAnswers: SurveyAnswers = this.answers) {
    const answers = this.chronoSort(rawAnswers)
    const result: SurveyAnswersMapperItem[] = []
    Object.keys(answers).forEach((pageName) => {
      Object.keys(answers[pageName]).forEach((elementName) => {
        const pageIndex = this.survey.pages.findIndex(
          (page) => page.name === pageName
        )
        if (pageIndex !== -1) {
          const elementIndex = this.survey.pages[pageIndex].elements.findIndex(
            (el) => el.name === elementName
          )
          result.push({
            question: this.survey.pages[pageIndex].elements[elementIndex],
            answer: answers[pageName][elementName],
            pageName: pageName,
            elementName: elementName
          })
        } else {
          console.error(
            'There is answers, that is not associated with any question.',
            pageName,
            elementName
          )
        }
      })
    })
    return result
  }

  /**
   * It will create a new survey object with filled values from the provided answers
   */
  mergedSurveyAnswersCopy() {
    const copy = JSON.parse(JSON.stringify(this.survey))
    Object.keys(this.answers).forEach((pageName) => {
      Object.keys(this.answers[pageName]).forEach((elementName) => {
        const pageIndex = this.survey.pages.findIndex(
          (page) => page.name === pageName
        )
        if (pageIndex !== -1) {
          const elementIndex = this.survey.pages[pageIndex].elements.findIndex(
            (el) => el.name === elementName
          )
          if (elementIndex !== -1) {
            copy.pages[pageIndex]!.elements[elementIndex]!.value =
              this.answers[pageName][elementName].value
            copy.pages[pageIndex]!.elements[elementIndex]!.prefilled = true
          }
        }
      })
    })
    return copy
  }

  /**
   * It will ensure to return the answers ordered by the survey questions sequence.
   */
  chronoSort(answers: SurveyAnswers = this.answers) {
    const orderKeys = this.survey.pages.map((page) => page.name)
    return Object.fromEntries(
      orderKeys
        .filter((key) => key in answers)
        .map((key) => [key, answers[key]])
    ) as SurveyAnswers
  }
}
